// The gestures of the onboarding todo (#120): what they are, how they group,
// and whether each is done. Each is derived from the org's real state, so a
// line checks itself when the gesture is made elsewhere, whatever card the org
// was created from: with a template, the gestures it already made arrive
// checked; an "Empty" org starts with none.

// Members with a photo needed to check the photos line
const PHOTOS_TO_CHECK = 2

export type OnboardingTodoItemKey =
  | 'role'
  | 'roleMember'
  | 'purpose'
  | 'accountabilities'
  | 'checklist'
  | 'indicators'
  | 'invite'
  | 'photos'
  | 'recurringMeeting'
  | 'meeting'
  | 'thread'
  | 'task'

export type OnboardingTodoGroupKey = 'structure' | 'team' | 'meetings' | 'act'

export interface OnboardingTodoGroup {
  key: OnboardingTodoGroupKey
  items: OnboardingTodoItemKey[]
}

// The order they are shown in, under one heading per group
export const onboardingTodoGroups: readonly OnboardingTodoGroup[] = [
  {
    key: 'structure',
    items: [
      'role',
      'roleMember',
      'purpose',
      'accountabilities',
      'checklist',
      'indicators',
    ],
  },
  { key: 'team', items: ['invite', 'photos'] },
  { key: 'meetings', items: ['recurringMeeting', 'meeting'] },
  { key: 'act', items: ['thread', 'task'] },
]

// What the todo reads from OrgData: the active circles and members
export interface OnboardingOrgState {
  circles: ReadonlyArray<{ id: string; parentId?: string | null }>
  members: ReadonlyArray<{
    inviteEmail?: string | null
    userId?: string | null
    picture?: string | null
  }>
  membersOf(circleId: string): readonly unknown[]
}

// What the todo reads from the onboardingActivity subscription
export interface OnboardingActivityState {
  hasRecurringMeeting: boolean
  hasHeldMeeting: boolean
  hasThread: boolean
  hasTask: boolean
  hasPurpose: boolean
  hasAccountabilities: boolean
  hasChecklist: boolean
  hasIndicators: boolean
}

export function onboardingTodoItems(
  org: OnboardingOrgState,
  activity: OnboardingActivityState
): Record<OnboardingTodoItemKey, boolean> {
  const { circles, members } = org
  return {
    // A placed role is a circle: any beyond the root one
    role: circles.length > 1,
    // Outside the root circle: an "Empty" org seats its creator there
    roleMember: circles.some(
      (circle) => !!circle.parentId && org.membersOf(circle.id).length > 0
    ),
    purpose: activity.hasPurpose,
    accountabilities: activity.hasAccountabilities,
    checklist: activity.hasChecklist,
    indicators: activity.hasIndicators,
    invite:
      members.some((m) => !!m.inviteEmail) ||
      members.filter((m) => !!m.userId).length >= 2,
    // A couple of faces are enough to bring the org chart to life: a photo for
    // every member would be out of reach in a large team
    photos:
      members.length > 0 &&
      members.filter((m) => !!m.picture).length >=
        Math.min(PHOTOS_TO_CHECK, members.length),
    // The setup only creates meeting templates: scheduling is left to the team
    recurringMeeting: activity.hasRecurringMeeting,
    meeting: activity.hasHeldMeeting,
    thread: activity.hasThread,
    task: activity.hasTask,
  }
}
